<template>
    <div>
      <label>{{ label }}</label>
      <!-- width/height are attributes, not CSS: they size the drawing buffer, and
           the scoped .signature-canvas rule sizes the box. The inline style that
           used to duplicate that rule is gone -- inline wins over any class, so it
           would have kept the border black while invalid. -->
      <canvas ref="canvas" :class="['signature-canvas', { 'is-invalid': error }]" width="480" height="150"></canvas>
      <input type="hidden" :name="name" :id="canvasId" :value="value" />
      <button class="ms-2 btn-icon bg-danger-subtle text-danger-emphasis" type="button" :title="$t('modal.clear')" @click="clearCanvas"><i class="fa-regular fa-trash-can"></i></button>
    </div>
  </template>
  
  <script>
  export default {
    props: {
      name: {
        type: String,
        required: true
      },
      value: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      // The root is a wrapper div, so this has to go on the canvas itself.
      error: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        canvasId: `canvas_${Math.random().toString(36).substr(2, 9)}`
      };
    },
    mounted() {
      this.initializeCanvas();
    },
    watch: {
      value(newValue) {
        this.loadImage(newValue);
      }
    },
    methods: {
      initializeCanvas() {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        let isDrawing = false;
  
        const startDrawing = (event) => {
          isDrawing = true;
          ctx.beginPath();
          ctx.moveTo(event.offsetX || event.touches[0].clientX - canvas.getBoundingClientRect().left, 
                     event.offsetY || event.touches[0].clientY - canvas.getBoundingClientRect().top);
          event.preventDefault();
        };
  
        const draw = (event) => {
          if (isDrawing) {
            ctx.lineTo(event.offsetX || event.touches[0].clientX - canvas.getBoundingClientRect().left, 
                       event.offsetY || event.touches[0].clientY - canvas.getBoundingClientRect().top);
            ctx.stroke();
            event.preventDefault();
          }
        };
  
        const stopDrawing = (event) => {
          if (isDrawing) {
            isDrawing = false;
            ctx.closePath();
            this.updateSignature();
            event.preventDefault();
          }
        };
  
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseleave', stopDrawing);
  
        canvas.addEventListener('touchstart', startDrawing);
        canvas.addEventListener('touchmove', draw);
        canvas.addEventListener('touchend', stopDrawing);
  
        this.loadImage(this.value);
      },
      clearCanvas() {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Not updateSignature(): toDataURL on an empty canvas still returns a
        // valid data URL -- a fully transparent PNG -- so the field would read as
        // signed, satisfying required-field validation and prompting the save
        // warning about a signature that is no longer there.
        this.$emit('update:value', '');
      },
      updateSignature() {
        const canvas = this.$refs.canvas;
        const dataURL = canvas.toDataURL('image/png');
        this.$emit('update:value', dataURL);
      },
      loadImage(dataURL) {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!dataURL) return;
        const image = new Image();
        image.src = dataURL;
        image.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Scale the image to fit the canvas while preserving aspect ratio, centered
          const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
          const drawWidth = image.width * scale;
          const drawHeight = image.height * scale;
          const dx = (canvas.width - drawWidth) / 2;
          const dy = (canvas.height - drawHeight) / 2;
          ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
        };
      }
    }
  };
  </script>
  
  <style scoped>
  .signature-canvas {
    width: 480px;
    height: 150px;
    border: 1px solid #000;
    /* Strokes are drawn in black and submitted as-is, so the canvas has to stay
       light in both colour modes or the signature disappears. */
    background-color: var(--bs-white);
  }

  .signature-canvas.is-invalid {
    border-color: red;
  }

  /* Dark mode shows the pad inverted — black backdrop, white strokes. This is a
     CSS filter, so it changes rendering only and never the canvas bitmap: the
     hidden input still carries the same black-on-white toDataURL() the API and
     the server-rendered PDF expect. Here the canvas *is* the bordered element,
     so the filter catches its border too; a mid grey is near enough its own
     inverse to stay neutral, and the invalid state is pre-inverted so it still
     reads as red. */
  [data-bs-theme='dark'] .signature-canvas {
    filter: invert(1);
    border-color: #8A8A8A;
  }

  [data-bs-theme='dark'] .signature-canvas.is-invalid {
    border-color: cyan; /* inverts to red */
  }
  </style>
  